import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MonitorSmartphone, Loader2, LogOut, Trash2, MapPin, ChevronDown, History } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

function LoginHistory({ deviceId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/auth/devices/${deviceId}/logins`).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [deviceId]);
  if (rows === null) return <div className="py-2 pl-1 text-xs text-muted-foreground">Loading sign-ins…</div>;
  if (rows.length === 0) return <div className="py-2 pl-1 text-xs text-muted-foreground" data-testid={`device-logins-empty-${deviceId}`}>No sign-ins recorded yet for this device.</div>;
  return (
    <ul className="mt-1 space-y-1 rounded-lg bg-secondary/50 p-2 text-xs" data-testid={`device-logins-${deviceId}`}>
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`login-event-${r.id}`}>
          <span className="tabular-nums text-foreground">{formatDateTime(r.at)}</span>
          <span className="text-muted-foreground">{r.location || "Location unknown"}</span>
          <span className="text-muted-foreground">IP {r.ip}</span>
          {r.new_device && <Badge className="border-amber-200 bg-amber-50 font-normal text-amber-800">first sign-in</Badge>}
        </li>
      ))}
    </ul>
  );
}

export function TrustedDevicesCard() {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/auth/devices").then(({ data }) => setDevices(data)).catch(() => setDevices([]));
  useEffect(() => { load(); }, []);

  const forget = async (d) => {
    setBusy(d.id);
    try { await api.delete(`/auth/devices/${d.id}`); toast.success(`Forgot ${d.label}. Next sign-in from it will alert you.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not forget device"); }
    finally { setBusy(null); }
  };

  const signOutEverywhere = async () => {
    setBusy("all");
    try { await api.post("/auth/sign-out-everywhere"); toast.success("Signed out of every other device. This session stays active."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not sign out other sessions"); }
    finally { setBusy(null); }
  };

  return (
    <Card className="rounded-xl border bg-card p-6" data-testid="trusted-devices-card">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <MonitorSmartphone className="h-5 w-5 text-primary" />
          <div>
            <h2 className="h-display text-lg font-semibold text-foreground">Recognised devices</h2>
            <p className="text-xs text-muted-foreground">Browsers that have signed in to your account. A sign-in from anything else emails you.</p>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={busy === "all"} data-testid="sign-out-everywhere">
              {busy === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />} Sign out everywhere
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="sign-out-everywhere-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Sign out of every other device?</AlertDialogTitle>
              <AlertDialogDescription>Every other browser and phone will need to sign in again. You stay signed in here.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sign-out-everywhere-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={signOutEverywhere} data-testid="sign-out-everywhere-confirm">Sign out other devices</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {devices === null ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : devices.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground" data-testid="devices-empty">No devices recorded yet - your next sign-in will appear here.</p>
      ) : (
        <ul className="divide-y" data-testid="devices-list">
          {devices.map((d) => (
            <li key={d.id} className="py-3" data-testid={`device-${d.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {d.label}
                    {d.is_current && <Badge className="border-emerald-200 bg-emerald-50 font-normal text-emerald-700" data-testid={`device-current-${d.id}`}>This device</Badge>}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    {d.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{d.location}</span>}
                    <span>Last seen {formatDateTime(d.last_seen)}</span>
                    {d.last_ip && <span>IP {d.last_ip}</span>}
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground" onClick={() => setExpanded((x) => (x === d.id ? null : d.id))} aria-expanded={expanded === d.id} data-testid={`device-history-${d.id}`}>
                  <History className="h-3.5 w-3.5" /> Sign-ins <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === d.id ? "rotate-180" : ""}`} />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-red-600" disabled={busy === d.id} onClick={() => forget(d)} data-testid={`device-forget-${d.id}`}>
                  {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Forget
                </Button>
              </div>
              {expanded === d.id && <LoginHistory deviceId={d.id} />}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
